import React from 'react';
import { isNaN, isFinite } from 'lodash';
import { Box, Grid } from '@mui/material';
import {
  IProjectColors,
  IProjectSpaceTypeCategory,
  IProjectSpaceTypeSubcategory,
} from 'spw-redux/modules/projects/types';
import {
  AlignEndColumn,
  Column,
  SpaceBetweenRow,
} from 'spw-shared/globalComponents/grid';
import { asPercentageRounded, numberWithCommas } from 'spw-shared/utils';
import {
  INDIVIDUAL_WORKSPACES_CATEGORY,
  NO_DATA_TO_SHOW,
  SUPPORT_SPACES_CATEGORY,
} from 'spw-constants';
import KeyboardArrowUp from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDown from '@mui/icons-material/KeyboardArrowDown';
import {
  Category,
  IProjectSpaceType,
  Subcategory,
} from 'spw-redux/modules/space_types/types';
import { PSCardGroupHeader } from 'spw-shared/globalComponents';
import useUnitConversion from 'spw-components/unit-conversion/hooks';
import {
  BigKey,
  CardTitle,
  BoldValue,
  Card,
} from '../macro-single-page-summary/styles';
import { UsfMixBar } from './usf-mix-bar';
import { ChildUsfMixRow } from './child-usf-mix-row';
import { EmptyListMessage } from '../../../../components/project-details-tabs/program-summary/shared/empty-list-message';
import { EmptyListBoxWrapper } from '../../../../components/project-details-tabs/program-summary/shared/styles';
import { PlainWorksheet } from '../../../../models/Worksheet';
import WorksheetRow from '../../../../models/WorksheetRow';
import { useWorksheetRowsByCategory } from './hooks/use-worksheet-rows-by-category';
import { RowContainer, SubcategoryContainer } from './style';

const UsfMixRow = ({
  index,
  row,
  worksheet,
  projectColors,
  category,
  subcategory,
  detailedIndividualConfig,
  totalCategoryUsf,
  isMacro,
  open = false,
  onClickSpaceIcon,
  projectSpaceTypes,
  viewOnly,
  clientFacingNamesAreVisible,
  isIndividualWorkspacesCard,
}: {
  index: number;
  row: WorksheetRow;
  worksheet: PlainWorksheet;
  projectColors: IProjectColors;
  detailedIndividualConfig?: boolean;
  category: Category | Subcategory;
  subcategory?: Subcategory;
  totalCategoryUsf?: number;
  isMacro?: boolean;
  open: boolean;
  onClickSpaceIcon: (e: Element) => void;
  projectSpaceTypes: { [id: number]: IProjectSpaceType };
  viewOnly: boolean;
  clientFacingNamesAreVisible?: boolean;
  isIndividualWorkspacesCard?: boolean;
}) => {
  let total = row.totalUsf;

  if (!row.child && (detailedIndividualConfig || row.isStorageSupportSpace)) {
    total = worksheet.spaceTypesTotals[row.uniqueKey]?.USF;
  }
  let percent: string | number = total / totalCategoryUsf;

  if (isNaN(percent) || !isFinite(percent)) {
    percent = NO_DATA_TO_SHOW;
  }
  if (row.child && !open) return null;
  if (row.child) {
    return (
      <ChildUsfMixRow
        row={row}
        percentage={percent}
        total={total}
        isMacro={isMacro}
        avgUsf={row.usf}
        shouldHideAvgUSF={row.isStorageSupportSpace}
        testingPrefix={`programming_ps_usf_mix_row_child_${row?.spaceType?.id}`}
        shouldShowClientFacingName={clientFacingNamesAreVisible}
        isIndividualWorkspacesCard={isIndividualWorkspacesCard}
      />
    );
  }

  return (
    <RowContainer
      key={`row-${row?.spaceType?.id}`}
      className={
        open && index !== 0 && category === INDIVIDUAL_WORKSPACES_CATEGORY
          ? 'show-border-top'
          : ''
      }
    >
      {row.isStorageSupportSpace ? (
        <PSCardGroupHeader>
          <Box display="flex" alignItems="center">
            <span>{row.spaceType.name}</span>
          </Box>
        </PSCardGroupHeader>
      ) : null}
      <UsfMixBar
        row={row}
        viewOnly={viewOnly}
        projectSpaceTypes={projectSpaceTypes}
        onClickSpaceIcon={onClickSpaceIcon}
        category={category}
        subcategory={subcategory}
        projectColors={projectColors}
        key={`row-bar-${row?.spaceType?.id}`}
        projectSpaceType={row.projectSpaceType}
        spaceType={row.spaceType}
        percentage={percent}
        total={total}
        averageUSF={row.usf}
        isMacro={isMacro}
        detailedIndividualConfig={detailedIndividualConfig}
        shouldHideAvgUSF={row.isStorageSupportSpace}
        testingPrefix={`programming_ps_usf_mix_row_${row?.spaceType?.id}`}
        clientFacingNamesAreVisible={clientFacingNamesAreVisible}
      />
    </RowContainer>
  );
};

const CategoryUsfMix = ({
  projectSpaceTypeCategory,
  worksheet,
  setOpen,
  open,
  detailedIndividualConfig,
  isMacro,
  projectColors,
  isRenderingForExport,
  onClickSpaceIcon,
  projectSpaceTypes,
  viewOnly,
  clientFacingNamesAreVisible,
}: {
  projectSpaceTypeCategory: IProjectSpaceTypeCategory;
  worksheet: PlainWorksheet;
  setOpen?: () => void;
  open?: boolean;
  detailedIndividualConfig?: boolean;
  isMacro?: boolean;
  projectColors: IProjectColors;
  isRenderingForExport?: boolean;
  onClickSpaceIcon?: (e: Element) => void;
  projectSpaceTypes: { [id: number]: IProjectSpaceType };
  viewOnly: boolean;
  clientFacingNamesAreVisible?: boolean;
}) => {
  const { getUnit, convertArea } = useUnitConversion();
  if (!worksheet) return null;
  const { category } = projectSpaceTypeCategory.defaultSpaceTypeCategory;
  const { rowsBySubcategory } = worksheet;

  const {
    categoryRows,
    hasRowsBelongingToASubcategory,
  } = useWorksheetRowsByCategory(projectSpaceTypeCategory, worksheet, open);

  const totalCategoryUsf = worksheet?.categoriesTotals?.[category]?.USF;

  const paddingBottom =
    (worksheet.hasWorkspacesNotIncludedInTotalSeats &&
      category === INDIVIDUAL_WORKSPACES_CATEGORY) ||
    category === SUPPORT_SPACES_CATEGORY
      ? 46
      : 16;

  const isIndividualWorkspacesCard =
    category === INDIVIDUAL_WORKSPACES_CATEGORY;
  const testingPrefix = `programming_ps_${category}_usf_mix`;

  return (
    <Card
      style={{
        boxSizing: 'border-box',
        paddingBottom,
        height: '100%',
      }}
    >
      <Box display="flex" flexDirection="column" style={{ height: '100%' }}>
        <SpaceBetweenRow>
          <CardTitle
            style={{ width: 'calc(100% - 60px)' }}
            data-testid={`${testingPrefix}_title`}
          >
            {projectSpaceTypeCategory.name.toUpperCase()} {getUnit('area')} MIX
          </CardTitle>
          {(detailedIndividualConfig || category === SUPPORT_SPACES_CATEGORY) &&
            !isRenderingForExport && (
              <div
                role="button"
                onClick={setOpen}
                style={{ cursor: 'pointer', marginTop: '-8px' }}
                aria-hidden="true"
              >
                {open ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
              </div>
            )}
        </SpaceBetweenRow>
        <Grid container>
          <Grid item xs={6}>
            <div>
              <BigKey style={{ paddingTop: 16 }}>
                {getUnit('area')} Totals
              </BigKey>
            </div>
          </Grid>
          <Grid item xs={6}>
            <Grid container>
              <Grid item xs={4}>
                <AlignEndColumn>
                  <BoldValue data-testid={`${testingPrefix}_totalPercentage`}>
                    {asPercentageRounded(
                      totalCategoryUsf / totalCategoryUsf,
                      false
                    )}
                  </BoldValue>
                  <BigKey>{getUnit('area')}</BigKey>
                </AlignEndColumn>
              </Grid>
              <Grid
                item
                xs={4}
                style={{
                  paddingRight: isMacro ? '' : '16px',
                  paddingBottom: '8px',
                }}
              >
                <AlignEndColumn>
                  <BoldValue data-testid={`${testingPrefix}_totalCategoryUsf`}>
                    {numberWithCommas(
                      Math.round(convertArea(totalCategoryUsf)),
                      false,
                      true
                    )}
                  </BoldValue>
                  <BigKey>{getUnit('area')}</BigKey>
                </AlignEndColumn>
              </Grid>
              <Grid
                item
                xs={4}
                style={{
                  borderLeft: !isMacro ? '1px solid #E0E0E0' : undefined,
                  paddingBottom: '8px',
                }}
              >
                <AlignEndColumn style={{ paddingTop: 16 }}>
                  <BigKey>Unit Size</BigKey>
                </AlignEndColumn>
              </Grid>
            </Grid>
          </Grid>
        </Grid>

        <PSCardGroupHeader
          className={`${
            projectSpaceTypeCategory.subcategories?.length > 0 &&
            !categoryRows.length
              ? 'mb-8'
              : ''
          }`}
        >
          <Box
            display="flex"
            alignItems="center"
            data-testid={`${testingPrefix}_cat_header`}
          >
            <span>{projectSpaceTypeCategory.name.toUpperCase()}</span>
          </Box>
        </PSCardGroupHeader>
        <Column
          style={
            !hasRowsBelongingToASubcategory
              ? { flexGrow: 1, justifyContent: 'center' }
              : {}
          }
        >
          {categoryRows?.length
            ? categoryRows.map((categoryRow: WorksheetRow, index: number) => {
                return (
                  <UsfMixRow
                    viewOnly={viewOnly}
                    projectSpaceTypes={projectSpaceTypes}
                    onClickSpaceIcon={onClickSpaceIcon}
                    index={index}
                    key={categoryRow.id}
                    row={categoryRow}
                    worksheet={worksheet}
                    projectColors={projectColors}
                    category={category}
                    detailedIndividualConfig={detailedIndividualConfig}
                    totalCategoryUsf={totalCategoryUsf}
                    isMacro={isMacro}
                    open={open}
                    clientFacingNamesAreVisible={clientFacingNamesAreVisible}
                    isIndividualWorkspacesCard={isIndividualWorkspacesCard}
                  />
                );
              })
            : !hasRowsBelongingToASubcategory && (
                <EmptyListBoxWrapper>
                  <EmptyListMessage />
                </EmptyListBoxWrapper>
              )}
        </Column>
        {projectSpaceTypeCategory.subcategories?.length > 0 && (
          <SubcategoryContainer>
            {projectSpaceTypeCategory.subcategories?.map(
              (subcategory: IProjectSpaceTypeSubcategory) => {
                const {
                  category: defaultSubcategory,
                } = subcategory.defaultSpaceTypeCategory;
                return (
                  rowsBySubcategory[defaultSubcategory]?.length > 0 && (
                    <div key={`${subcategory.id}`}>
                      <PSCardGroupHeader>
                        <Box display="flex" alignItems="center">
                          <span>{subcategory.name}</span>
                        </Box>
                      </PSCardGroupHeader>
                      <Column>
                        {rowsBySubcategory[defaultSubcategory].map(
                          (row: WorksheetRow, index: number) => (
                            <UsfMixRow
                              viewOnly={viewOnly}
                              projectSpaceTypes={projectSpaceTypes}
                              onClickSpaceIcon={onClickSpaceIcon}
                              index={index}
                              key={row.id}
                              row={row}
                              worksheet={worksheet}
                              projectColors={projectColors}
                              subcategory={defaultSubcategory}
                              category={category}
                              detailedIndividualConfig={
                                detailedIndividualConfig
                              }
                              totalCategoryUsf={totalCategoryUsf}
                              isMacro={isMacro}
                              open={open}
                              clientFacingNamesAreVisible={
                                clientFacingNamesAreVisible
                              }
                              isIndividualWorkspacesCard={
                                isIndividualWorkspacesCard
                              }
                            />
                          )
                        )}
                      </Column>
                    </div>
                  )
                );
              }
            )}
          </SubcategoryContainer>
        )}
      </Box>
    </Card>
  );
};

// Performance optimization
const MemoizedCategoryUsfMix = React.memo(
  CategoryUsfMix,
  (prevProps, nextProps) => {
    const {
      category,
    } = prevProps.projectSpaceTypeCategory.defaultSpaceTypeCategory;

    const prevCategoryUSF =
      prevProps.worksheet?.categoriesTotals?.[category]?.USF;
    const nextCategoryUSF =
      nextProps.worksheet?.categoriesTotals?.[category]?.USF;

    const prevProjectSpaceTypes = prevProps.projectSpaceTypes;
    const nextProjectSpaceTypes = nextProps.projectSpaceTypes;

    const prevWorksheetRows = prevProps.worksheet.rows;
    const nextWorksheetRows = nextProps.worksheet.rows;

    return (
      nextCategoryUSF === prevCategoryUSF &&
      prevProps.open === nextProps.open &&
      prevProjectSpaceTypes === nextProjectSpaceTypes &&
      JSON.stringify(prevWorksheetRows) === JSON.stringify(nextWorksheetRows) &&
      prevProps.clientFacingNamesAreVisible ===
        nextProps.clientFacingNamesAreVisible
    );
  }
);

MemoizedCategoryUsfMix.whyDidYouRender = true;

export { MemoizedCategoryUsfMix as CategoryUsfMix };
